// Mirrors accounts/internal/handlers/sleeverealignment/types.go. Reads are
// per-account; the state-changing actions are top-level body-list routes.

/**
 * Which sleeves are drawn from. `over_target` pulls every sleeve above its
 * target; `over_max` only those above their band ceiling.
 */
export enum SleeveRealignmentSourceScope {
  OverMax = 'over_max',
  OverTarget = 'over_target',
}

/** The accounts handler defaults to `over_max`; the frontend wants every sleeve above target. */
export const DEFAULT_SOURCE_SCOPE = SleeveRealignmentSourceScope.OverTarget

/** Shape of the account's drift. `SELL_REQUIRED` is the only multi-step case. */
export enum SleeveRealignmentScenario {
  AtTarget = 'AT_TARGET',
  MoveCashOnly = 'MOVE_CASH_ONLY',
  SellRequired = 'SELL_REQUIRED',
  NoOverweight = 'NO_OVERWEIGHT',
}

/** Money fields arrive as decimal strings, not numbers — parse before arithmetic. */
export interface SleeveRealignmentPreviewLeg {
  sleeve_id: string
  current_value: string
  target_value: string
  max_value: string
  amount: string
  /** The sleeve's cash is short of its raise, so it runs negative until a sell settles. */
  goes_negative: boolean
}

/** One managed sleeve's projection — every managed sleeve, not just the sources. */
export interface SleeveRealignmentPreviewSleeve {
  sleeve_id: string
  current_value: string
  target_value: string
  est_after_value: string
  /** Signed: positive means the sleeve gains value, negative means it gives some up. */
  delta: string
}

export interface SleeveRealignmentPreview {
  source_scope: SleeveRealignmentSourceScope
  funding_sleeve_id: string
  total_amount: string
  scenario: SleeveRealignmentScenario
  /** Per-source raises. Empty in the `AT_TARGET` / `NO_OVERWEIGHT` scenarios. */
  legs: SleeveRealignmentPreviewLeg[]
  sleeves: SleeveRealignmentPreviewSleeve[]
}

interface SleeveRealignmentInitiateLeg {
  sleeve_id: string
  amount: string
}

interface SleeveRealignmentInitiateItem {
  account_id: string
  source_scope: SleeveRealignmentSourceScope
  notes?: string | null
  legs: SleeveRealignmentInitiateLeg[]
}

/** Initiate takes items, not the flat `account_ids` the other actions use. */
export interface SleeveRealignmentInitiateRequest {
  items: SleeveRealignmentInitiateItem[]
}

/** Complete/cancel/rebalance target each account's single active realignment. */
export interface SleeveRealignmentBulkActionRequest {
  account_ids: string[]
}

/** Full wire contract; the frontend only ever branches on the active one. */
export type SleeveRealignmentRequestStatus =
  | 'INITIATED'
  | 'COMPLETED'
  | 'CANCELED'

/** The single non-terminal status — the only realignment a user can act on. */
export const REALIGNMENT_STATUS_INITIATED: SleeveRealignmentRequestStatus =
  'INITIATED'

/** DB-enforced pair: SELL raises sleeve -> funding, BUY reverses funding -> sleeve. */
type SleeveRealignmentLegSide = 'SELL' | 'BUY'

/** `amount` is always positive; `side` carries the direction. */
interface SleeveRealignmentLeg {
  id: string
  sleeve_id: string
  side: SleeveRealignmentLegSide
  amount: string
  intraday_cash_transfer_id: string | null
}

/** A sleeve still cash-negative; `amount` is the outstanding shortfall (positive). */
interface SleeveRealignmentDeficit {
  sleeve_id: string
  amount: string
}

/** `ready`/`deficits` are derived from live cash and meaningless once terminal. */
export interface SleeveRealignmentStatus {
  id: string
  account_id: string
  source_scope: SleeveRealignmentSourceScope
  request_status: SleeveRealignmentRequestStatus
  initiated_by_user_id: string | null
  initiated_at: string
  completed_by_user_id: string | null
  /** completed_at or canceled_at; null while INITIATED. */
  ended_at: string | null
  notes: string | null
  legs: SleeveRealignmentLeg[]
  /** No sleeve in the account is cash-negative, so step 2 can run. */
  ready: boolean
  deficits: SleeveRealignmentDeficit[]
}

interface SleeveRealignmentCancelPreviewLeg {
  sleeve_id: string
  current_value: string
  amount_returned: string
  est_after_value: string
}

export interface SleeveRealignmentCancelPreview {
  funding_sleeve_id: string
  total_reserved_returned: string
  legs: SleeveRealignmentCancelPreviewLeg[]
}

interface SleeveRealignmentRebalancePreviewSleeve {
  sleeve_id: string
  current_value: string
  target_value: string
  /** Signed cash delta: positive receives, negative is swept back to funding. */
  amount: string
  resulting_value: string
}

export interface SleeveRealignmentRebalancePreview {
  realignment_id: string
  funding_sleeve_id: string
  released_reserve: string
  funding_resulting_cash: string
  sleeves: SleeveRealignmentRebalancePreviewSleeve[]
}

interface SleeveRealignmentBulkItemResult {
  account_id: string
  realignment_id?: string
  /** Skips carry an actionable message; failures are always `internal error`. */
  reason?: string
}

/**
 * A 200 does not mean the action succeeded — the batch is partitioned per
 * account. Resolve one account's result with `resolveBulkOutcome`.
 */
export interface SleeveRealignmentBulkActionResponse {
  processed: SleeveRealignmentBulkItemResult[]
  skipped: SleeveRealignmentBulkItemResult[]
  failed: SleeveRealignmentBulkItemResult[]
}
